/**
 * Config game: mặc định là `defaults.json` của @ml/config (đóng gói sẵn trong bundle, chơi được
 * offline). `?config=<url>` (đường sẵn cho live-config M2) tải JSON từ URL, nhận một trong ba dạng:
 * bản phát hành `{configVersion, configHash, config}` (kiểm hash), config đầy đủ, hoặc bản vá một
 * phần (ghép lên defaults). Lỗi tải/parse → giữ defaults và báo lý do (không chặn vào game).
 */
import { configHash, defaultConfig, mergeConfig, parseRelease, safeParseConfig } from '@ml/config';
import type { Config } from '@ml/config';

export type LoadedConfig = {
  readonly config: Config;
  readonly source: 'defaults' | 'url';
  readonly url?: string;
  readonly hash: string;
  readonly error?: string;
};

function isObject(v: unknown): v is Record<string, unknown> {
  return v !== null && typeof v === 'object' && !Array.isArray(v);
}

/** Nhận JSON đã tải và trả Config (throw nếu sai). */
export function configFromJson(json: unknown): Config {
  if (!isObject(json)) throw new Error('config phải là object JSON');
  if ('configHash' in json && 'config' in json) return parseRelease(json).config;
  const full = safeParseConfig(json);
  if (full.ok) return full.config;
  return mergeConfig(json as Parameters<typeof mergeConfig>[0]);
}

export async function loadConfig(search = location.search): Promise<LoadedConfig> {
  const params = new URLSearchParams(search);
  const url = params.get('config');
  let config = defaultConfig();
  let out: LoadedConfig = { config, source: 'defaults', hash: '' };
  if (url) {
    try {
      const res = await fetch(url, { cache: 'no-store' });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      config = configFromJson(await res.json());
      out = { config, source: 'url', url, hash: '' };
    } catch (e) {
      out = { config, source: 'defaults', url, hash: '', error: String(e) };
    }
  }
  // Tham số debug: `?speed=<pct>` đổi tốc độ anim (test e2e chạy nhanh).
  const speed = Number(params.get('speed'));
  if (Number.isFinite(speed) && speed > 0) {
    config = { ...config, ui: { ...config.ui, anim: { ...config.ui.anim, speedPct: speed } } };
  }
  return { ...out, config, hash: configHash(out.config) };
}
